import { useMemo, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs, type BreadcrumbItem } from '@/shared/lib/breadcrumbs-context'
import {
  adminSignoffMonitoringPath,
  departmentSignoffChainPath,
  departmentsPath,
  organizationPath,
} from '@/shared/lib/routes'
import {
  RETURN_TO_DEPARTMENTS,
  resolveReturnTo,
  type SignoffChainReturnTo,
} from '@/shared/lib/signoff-chain-return-to'
import type { SharedProps } from '@/shared/types/SharedProps'
import type WebSignoffChainPage from '@/shared/types/web/WebSignoffChainPage'

interface SignoffChainEditPageProps {
  department: { id: number; name: string; siteName: string | null }
  /** Raw origin from the server — re-allowlisted client-side before use (AC2/AC5). */
  returnTo?: string
  canEdit: boolean
  signoffChainPage: WebSignoffChainPage
}

type PageProps = SharedProps & SignoffChainEditPageProps

interface SignoffChainFormData {
  // Ordered steps; each step is an ordered list of alternate signer user ids ("Carol OR Michael").
  steps: number[][]
}

/** Server may attach whole-form messages under `base` (not a key of `SignoffChainFormData`). */
type SignoffChainFormErrors = Partial<Record<keyof SignoffChainFormData, string>> & {
  base?: string | string[]
}

type SignoffRole = WebSignoffChainPage['chain']['signoffRoles'][number]

/** Mirrors how the user reached this page so the breadcrumb and post-save redirect return them there. */
const originBreadcrumbs = (returnTo: SignoffChainReturnTo): BreadcrumbItem[] => {
  if (returnTo === RETURN_TO_DEPARTMENTS) {
    return [
      { label: t('nav.administration'), href: organizationPath() },
      { label: t('nav.departmentsAndUnits'), href: departmentsPath() },
      { label: t('nav.departments'), href: departmentsPath() },
    ]
  }

  return [
    { label: t('nav.administration'), href: organizationPath() },
    { label: t('nav.signoffMonitoring'), href: adminSignoffMonitoringPath() },
  ]
}

// Groups the flat, sort_order-ordered roles into steps. Roles sharing a sortOrder are alternates
// of one step (legacy signOffChainSortOrder grouping).
export const buildStepsFromRoles = (roles: SignoffRole[]): number[][] => {
  const bySortOrder = new Map<number, number[]>()
  for (const role of [...roles].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const bucket = bySortOrder.get(role.sortOrder) ?? []
    bucket.push(role.userId)
    bySortOrder.set(role.sortOrder, bucket)
  }
  return [...bySortOrder.keys()].sort((a, b) => a - b).map((order) => bySortOrder.get(order)!)
}

export const computeHasEffectiveChanges = (
  effectiveSteps: number[][],
  originalSteps: number[][],
): boolean => JSON.stringify(effectiveSteps) !== JSON.stringify(originalSteps)

const SignoffChainEdit = () => {
  const {
    department,
    returnTo: returnToProp,
    canEdit,
    signoffChainPage,
  } = usePage<PageProps>().props
  const returnTo = resolveReturnTo(returnToProp)
  const { chain, availableUsers } = signoffChainPage

  useBreadcrumbs([...originBreadcrumbs(returnTo), { label: t('signoffChain.pageTitlePrefix') }])

  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [optionalPickerStep, setOptionalPickerStep] = useState<number | null>(null)
  const [optionalUserId, setOptionalUserId] = useState<string>('')
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  // Signer pending removal-confirmation (REBUILD-411): removing a signer is destructive to the
  // approval sequence, so it is gated behind an explicit confirmation dialog.
  const [pendingRemoval, setPendingRemoval] = useState<{
    stepIndex: number
    memberIndex: number
  } | null>(null)

  const originalSteps = useMemo(() => buildStepsFromRoles(chain.signoffRoles), [chain.signoffRoles])

  const form = useForm<SignoffChainFormData>({ steps: originalSteps })

  const nameForUserId = (userId: number): string => {
    const role = chain.signoffRoles.find((signoffRole) => signoffRole.userId === userId)
    if (role) return role.userFullName
    const available = availableUsers.find((availableUser) => availableUser.id === userId)
    return available?.fullName ?? ''
  }

  const allSelectedUserIds = form.data.steps.flat()

  const chainUrl = departmentSignoffChainPath(department.id, { return_to: returnTo })

  const setSteps = (steps: number[][]) => form.setData('steps', steps)

  const hasEffectiveChanges = computeHasEffectiveChanges(form.data.steps, originalSteps)

  const moveStep = (fromIndex: number, toIndex: number) => {
    const updated = [...form.data.steps]
    const [moved] = updated.splice(fromIndex, 1)
    updated.splice(toIndex, 0, moved)
    setSteps(updated)
  }

  // Removing a step's primary promotes its first alternate; removing an alternate just drops it.
  const removeSigner = (stepIndex: number, memberIndex: number) => {
    const updated = form.data.steps
      .map((step, index) => (index === stepIndex ? step.filter((_, i) => i !== memberIndex) : step))
      .filter((step) => step.length > 0)
    setSteps(updated)
  }

  const confirmRemoveSigner = () => {
    if (pendingRemoval) removeSigner(pendingRemoval.stepIndex, pendingRemoval.memberIndex)
    setPendingRemoval(null)
  }

  const addStep = () => {
    const parsedUserId = parseInt(selectedUserId, 10)
    if (!parsedUserId) return
    if (allSelectedUserIds.includes(parsedUserId)) return
    setSteps([...form.data.steps, [parsedUserId]])
    setSelectedUserId('')
  }

  const addOptional = () => {
    const parsedUserId = parseInt(optionalUserId, 10)
    if (!parsedUserId || optionalPickerStep === null) return
    const updated = form.data.steps.map((step, index) =>
      index === optionalPickerStep ? [...step, parsedUserId] : step,
    )
    setSteps(updated)
    setOptionalUserId('')
    setOptionalPickerStep(null)
  }

  const handleSaveClick = () => {
    if (!hasEffectiveChanges) return
    // Defense-in-depth: button is disabled when either picker has a pending (un-Added) selection,
    // but guard here too in case the click is triggered programmatically.
    if (selectedUserId || optionalUserId) return

    if (originalSteps.length > 0) {
      setConfirmDialogOpen(true)
    } else {
      submitForm()
    }
  }

  const submitForm = () => {
    form.transform((data) => ({ signoff_chain: { ...data } }))
    form.patch(chainUrl)
    setConfirmDialogOpen(false)
  }

  const availableUsersForSelect = availableUsers.filter(
    (availableUser) => !allSelectedUserIds.includes(availableUser.id),
  )

  const formErrors = form.errors as SignoffChainFormErrors

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">
        {t('signoffChain.pageTitlePrefix')} — {department.name}
        {department.siteName && ` (${t('signoffChain.siteLabel')}: ${department.siteName})`}
      </h1>

      {form.data.steps.length === 0 ? (
        <p className="text-muted-foreground mb-6">{t('signoffChain.emptyState')}</p>
      ) : (
        <ol className="space-y-2 mb-6">
          {form.data.steps.map((step, stepIndex) => (
            <li key={stepIndex} className="rounded-md border bg-card px-4 py-3">
              <div className="flex items-start gap-3">
                <span className="text-sm font-medium text-muted-foreground w-6 shrink-0 pt-1">
                  {stepIndex + 1}.
                </span>
                <div className="flex-1 space-y-1">
                  {step.map((userId, memberIndex) => (
                    <div key={userId} className="flex items-center gap-2 text-sm">
                      {memberIndex > 0 && (
                        <span className="text-xs font-semibold text-muted-foreground">
                          {t('signoffChain.or')}
                        </span>
                      )}
                      <span className="flex-1">{nameForUserId(userId)}</span>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingRemoval({ stepIndex, memberIndex })}
                          aria-label={t('signoffChain.remove')}
                          className="text-destructive hover:text-destructive"
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  ))}
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        setOptionalPickerStep(stepIndex)
                        setOptionalUserId('')
                      }}
                    >
                      + {t('signoffChain.addOptional')}
                    </Button>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={stepIndex === 0}
                      onClick={() => moveStep(stepIndex, stepIndex - 1)}
                      aria-label={t('signoffChain.moveUp')}
                    >
                      ▲
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={stepIndex === form.data.steps.length - 1}
                      onClick={() => moveStep(stepIndex, stepIndex + 1)}
                      aria-label={t('signoffChain.moveDown')}
                    >
                      ▼
                    </Button>
                  </div>
                )}
              </div>

              {canEdit && optionalPickerStep === stepIndex && (
                <div className="mt-2 flex gap-2 pl-9">
                  <SearchableSelect
                    className="flex-1"
                    value={optionalUserId}
                    onValueChange={setOptionalUserId}
                    placeholder={t('signoffChain.selectSignerPlaceholder')}
                    options={availableUsersForSelect.map((availableUser) => ({
                      value: String(availableUser.id),
                      label: availableUser.fullName,
                    }))}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addOptional}
                    disabled={!optionalUserId}
                  >
                    {t('signoffChain.addSigner')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setOptionalPickerStep(null)}>
                    {t('signoffChain.confirmCancel')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {canEdit && availableUsersForSelect.length > 0 && (
        <div className="mb-8">
          <div className="flex gap-2">
            <SearchableSelect
              className="flex-1"
              value={selectedUserId}
              onValueChange={(selectedValue) => setSelectedUserId(selectedValue)}
              placeholder={t('signoffChain.selectSignerPlaceholder')}
              options={availableUsersForSelect.map((availableUser) => ({
                value: String(availableUser.id),
                label: availableUser.fullName,
              }))}
            />
            <Button onClick={addStep} disabled={!selectedUserId}>
              {t('signoffChain.addSigner')}
            </Button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t('signoffChain.addSignerHint')}</p>
        </div>
      )}

      {formErrors.base && (
        <p className="text-sm text-destructive mb-4">
          {Array.isArray(formErrors.base) ? formErrors.base.join(' ') : formErrors.base}
        </p>
      )}

      {canEdit ? (
        <Button
          onClick={handleSaveClick}
          disabled={form.processing || !hasEffectiveChanges || !!selectedUserId || !!optionalUserId}
        >
          {form.processing ? t('signoffChain.saving') : t('signoffChain.save')}
        </Button>
      ) : (
        <NoAccessNotice />
      )}

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('signoffChain.confirmTitle')}</DialogTitle>
            <DialogDescription>{t('signoffChain.confirmDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              {t('signoffChain.confirmCancel')}
            </Button>
            <Button onClick={() => submitForm()}>{t('signoffChain.confirmSave')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('signoffChain.removeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('signoffChain.removeConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('signoffChain.confirmCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveSigner}>
              {t('signoffChain.removeConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default SignoffChainEdit
